import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as loader from "../../components/Loading/loading";
import { createMaturityPool, detailPool, updateMaturityPool, updatePool } from "services/batch";
import { dropDownOptions } from "constant/options";
import {
	Button,
	DefaultSideBar,
	Header,
	Heading,
	Img,
	SelectBox,
	Text,
	TextArea,
} from "components";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TableEditBatch from "components/TableEditBatch";
import { getListMaturityPool, getRedeemHistoryPool } from "services/redeem";
import TableLogPoolReplacements from "components/TableLogPoolReplacements";
import TableListMaturityPool from "components/TableListMaturityPool";
import { PDFDownloadLink } from "@react-pdf/renderer";
import DetailPoolPdf from "./index-pdf";

const DetailPool = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const id = queryParams.get("id");
	const [data, setData] = useState();
	const [logHistory, setLogHistory] = useState();
	const [listMaturity, setListMaturity] = useState();
	const [action, setAction] = useState("");
	const [submitError, setSubmitError] = useState("");
	const [maturityDateError, setMaturityDateError] = useState("");
	const [reason, setReason] = useState("");
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(new Date());

	const _capitalize = useCallback((str) => {
		if (!str) return "";
		var splitStr = str.toLowerCase().replaceAll("_", " ").split(" ");
		for (var i = 0; i < splitStr.length; i++) {
			splitStr[i] =
				splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
		}
		return splitStr.join(" ");
	}, []);

	const _status = useMemo(() => {
		if (!data || !data.status) return "";
		return _capitalize(data.status);
	}, [data, _capitalize]);

	const _fetchData = useCallback(async (id) => {
		if (!id) return;

		loader.showLoader();
		try {
			const fetch = await detailPool(id);
			setData(fetch.data.pooling_data);

			const fetchHistory = await getRedeemHistoryPool(id);
			setLogHistory(fetchHistory.data.redeems);

			const fetchMaturity = await getListMaturityPool(id);
			setListMaturity(fetchMaturity.data.pool_maturity_history_data)
		} catch (error) {
			throw error;
		} finally {
			loader.hideLoader();
		}
	}, []);

	const _onUpdatePool = async (maturity) => {
		loader.showLoader();
		try {
			await updateMaturityPool(maturity)
			await updatePool({
				...data,
				status_maturity_data: maturity.status,
				pool_agreement: maturity.status === 'approved' ? maturity.pool_agreement : data.pool_agreement,
				active_date: maturity.status === 'approved' ? maturity.active_date : data.active_date,
				active_until: maturity.status === 'approved' ? maturity.active_until : data.active_until,
			})
			await _fetchData(id)
		} catch (error) {
			throw error;
		} finally {
			loader.hideLoader();
		}
	}

	const _parseDate = useCallback((date) => {
		try {
			const res = date.split("T")[0];
			if (res === "0001-01-01") {
				return "-";
			}
			return res;
		} catch (error) {
			return "-";
		}
	}, []);

	const _handleAction = useCallback(async () => {
		loader.showLoader();
		try {
			const newStatus =
				action === "rejected"
					? "new"
					: data.status === "pending_subscribe"
						? "subscribed"
						: "active";
			const date_start = startDate.toISOString().slice(0, 10);
			const date_end = endDate.toISOString().slice(0, 10);
			const response = await updatePool({
				...data,
				status: newStatus,
				activation_rejected_reason: (data.status === "pending_active" && newStatus === 'new' && reason !== '') ? reason : '',
				subscription_rejected_reason: (data.status === "pending_subscribe" && newStatus === 'new' && reason !== '') ? reason : '',
				active_date: newStatus === "active" ? date_start : data.active_date,
				active_until: newStatus === "active" ? date_end : data.active_until,
			});

			if (response) {
				// add insert maturity history on status approved
				if (action === "approved" && data.status === "pending_active") {
					await createMaturityPool({
						pool_id: data.id,
						active_date: date_start,
						active_until: date_end,
						status: 'approved',
						pool_agreement: data.pool_agreement,
						reason: ''
					});
				}
				navigate("/pool");
			}
		} catch (error) {
			console.log("error", error);
			setSubmitError(error.message || "An error occurred while updating the pool.");
		} finally {
			loader.hideLoader();
		}
	}, [data, action, _status, startDate, endDate, reason]);

	const _handleApproveMaturityDate = useCallback(async () => {
		loader.showLoader();
		try {
			const response = await updatePool({
				...data,
				update_maturity_data: { ...data.update_maturity_data, status: 'active' },
			});
			if (response) {
				navigate("/pool");
			}
		} catch (error) {
			setMaturityDateError(error);
		} finally {
			loader.hideLoader();
		}
	}, [data, action, _status, startDate, endDate, reason]);

	const _inputDate = useMemo(() => {
		return (
			<>
				<div className="flex flex-row justify-start w-full mt-2">
					<Heading
						size="lg"
						as="p"
						className="!text-blue_gray-900_01 !font-opensans"
					>
						Start Date:
					</Heading>
				</div>
				<DatePicker
					dateFormat="dd/MM/yyyy"
					className="w-full font-opensans !border-gray-300_01 !border !border-solid !rounded-lg !p-2 my-1"
					minDate={new Date()}
					selected={startDate}
					onSelect={setStartDate}
					onChange={setStartDate}
				/>
				<div className="flex flex-row justify-start w-full mt-2">
					<Heading
						size="lg"
						as="p"
						className="!text-blue_gray-900_01 !font-opensans"
					>
						End Date:
					</Heading>
				</div>
				<DatePicker
					dateFormat="dd/MM/yyyy"
					className="w-full font-opensans !border-gray-300_01 !border !border-solid !rounded-lg !p-2 my-1"
					minDate={startDate}
					selected={endDate}
					onSelect={setEndDate}
					onChange={setEndDate}
				/>
			</>
		);
	}, [startDate, endDate]);

	useEffect(() => {
		_fetchData(id);
	}, [id]);

	useEffect(() => {
		const date = new Date();
		date.setDate(date.getDate() + 60);
		setEndDate(date);
	}, []);

	return (
		<div className=" w-full relative bg-gray-100 pl-[81px] pt-[10vh]">
			<Header />
			<DefaultSideBar />
			<div className="flex flex-col gap-4">
				<div className="flex flex-col justify-center w-full ml-2 p-[43px] border-blue_gray-100_01 border border-solid bg-white-A700 rounded-[10px]">
					{data ? (
						<div className="grid grid-cols-3 gap-x-8 justify-between p-5 w-full">
							<div className="flex flex-col items-start justify-start w-full">
								<Heading size="2xl" as="h1">
									Pool Detail
								</Heading>
								<div className="flex flex-col gap-5 mt-8">
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Pool ID:
										</Text>
										<Heading as="h5">{data.id}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Pool Name:
										</Text>
										<Heading as="h5">{data.pool_name}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											OC Level:
										</Text>
										<Heading as="h5">{data.pool_value}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											OC Percentage:
										</Text>
										<Heading as="h5">{data.oc_value}%</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Indicative Return:
										</Text>
										<Heading as="h5">{data.indicative_return}%</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Originator ID:
										</Text>
										<Heading as="h6">{data.owner_id}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Originator Name:
										</Text>
										<Heading as="h6">{data.owner_name}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Fund Manager ID:
										</Text>
										<Heading as="h6">{(data.status === 'new' ? '-' : data.fund_manager_id) ?? '-'}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Fund Manager Name:
										</Text>
										<Heading as="h6">{(data.status === 'new' ? '-' : data.fund_manager_name) ?? "-"}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Active Start Date:
										</Text>
										<Heading as="h6">{_parseDate(data.active_date)}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Active End Date:
										</Text>
										<Heading as="h6">{_parseDate(data.active_until)}</Heading>
									</div>
								</div>
							</div>

							<div className="flex flex-col items-start justify-start w-full">
								<Heading size="2xl" as="h1">
									Issuer On Board
								</Heading>
								<div className="flex flex-col gap-5 mt-8">
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Issuer ID:
										</Text>
										<Heading as="h5">{data.company_id}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Name:
										</Text>
										<Heading as="h6">{data.company.name}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Email:
										</Text>
										<Heading as="h6">{data.company.email}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Phone:
										</Text>
										<Heading as="h6">{data.company.phone}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Rollover Date:
										</Text>
										<Heading as="h6">{() => {
											const date = data.roll_over_data?.updated_at?.substring(0, 10) ?? '-'
											return date === "0001-01-01" ? "-" : date;
										}}</Heading>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text size="xl" as="p" className="!text-blue_gray-700">
											Rollover Status:
										</Text>
										<Heading as="h6">{_capitalize(data.roll_over_data?.status)}</Heading>
									</div>
								</div>
							</div>

							<div className="flex flex-col items-start justify-start w-full">
								<div className="flex flex-col items-start justify-start gap-2">
									<Text size="xl" as="p" className="!text-blue_gray-700">
										Status:
									</Text>
									<Heading as="h6">{_status}</Heading>
								</div>
								<div className="flex flex-col gap-2  mt-8">
									<Text size="xl" as="p" className="!text-blue_gray-700">
										Agreements:
									</Text>
									{data.pool_agreement &&
										<Heading as="h6" className="hover:underline hover:cursor-pointer flex items-center" onClick={() => data.pool_agreement && window.open(data.pool_agreement, "_blank")}>
											<Img
												src="images/img_lets_icons_view_light.svg"
												alt="letsiconsview"
												className="h-[20px] w-[20px] mr-1"
											/>
											Master Agreement
										</Heading>
									}
									{data.transaction_agreement &&
										<Heading as="h6" className="hover:underline hover:cursor-pointer flex items-center" onClick={() => data.transaction_agreement && window.open(data.transaction_agreement, "_blank")}>
											<Img
												src="images/img_lets_icons_view_light.svg"
												alt="letsiconsview"
												className="h-[20px] w-[20px] mr-1"
											/>
											Transaction Agreement
										</Heading>
									}
									{data.term_sheet &&
										<Heading as="h6" className="hover:underline hover:cursor-pointer flex items-center" onClick={() => data.term_sheet && window.open(data.term_sheet, "_blank")}>
											<Img
												src="images/img_lets_icons_view_light.svg"
												alt="letsiconsview"
												className="h-[20px] w-[20px] mr-1"
											/>
											Term Sheet
										</Heading>
									}
								</div>
								{_status.includes("Pending") ? (
									<div className="flex flex-col items-center justify-start w-full max-w-[200px] gap-[7px] mt-8">
										<div className="flex flex-row justify-start w-full">
											<Heading
												size="lg"
												as="p"
												className="!text-blue_gray-900_01 !font-opensans"
											>
												Action
											</Heading>
										</div>
										<SelectBox
											indicator={
												<Img
													src="images/img_icon_field_clear.svg"
													alt="Icon / Field / Clear"
												/>
											}
											name="rejected"
											placeholder="Select Status"
											options={dropDownOptions}
											onChange={(value) => {
												setAction(value.value);
											}}
											disable={_status === "New"}
											className="w-full gap-px font-opensans border-gray-300_01 border border-solid !rounded-lg"
										/>

										{action === "rejected" ? (
											<>
												<div className="flex flex-row justify-start w-full mt-2">
													<Heading
														size="lg"
														as="p"
														className="!text-blue_gray-900_01 !font-opensans"
													>
														Reject Reason:
													</Heading>
												</div>
												<TextArea
													onChange={(e) => {
														setReason(e);
													}}
													name="input_one"
													placeholder="Enter your rejected reason"
													className="w-full text-blue_gray-500 mt-1"
												/>
											</>
										) : null}

										{action === 'approved' && _status === 'Pending Active' && _inputDate}

										<Button
											onClick={_handleAction}
											color="indigo_900"
											size="md"
											disable={action === ""}
											className="tracking-[0.40px] font-poppins uppercase font-medium  w-full mt-3"
										>
											Update
										</Button>
									</div>
								) : null}
								{submitError && (
									<Text as="p" className="text-red-500">
										{submitError}
									</Text>
								)}
								{(data.update_maturity_data && 'active_until' in data.update_maturity_data && data.update_maturity_data.status === 'pending') ?
									<>
										<div className="flex flex-col items-start justify-start gap-2 mt-3">
											<Text size="xl" as="p" className="!text-blue_gray-700">
												Request Update Maturity Date:
											</Text>
											<Heading as="h6">Active until {data.update_maturity_data.active_until}</Heading>
										</div>
										<Button
											onClick={_handleApproveMaturityDate}
											color="indigo_900"
											size="md"
											className="tracking-[0.40px] font-poppins uppercase font-medium  w-full mt-3"
										>
											Approve
										</Button>
										{maturityDateError && (
											<Text as="p" className="text-red-500">
												{maturityDateError}
											</Text>
										)}
									</> :
									null}
							</div>
						</div>
					) : null}
				</div>
				<div className="flex mt-5 flex-col justify-center w-full ml-2 border-blue_gray-100_01 border border-solid bg-white-A700 rounded-[10px]">
					<Heading size="2xl" as="h1" className="ml-[43px] mt-[43px]">
						List Assets
					</Heading>
					<TableEditBatch data={data?.pooling_data?.filter(p => p.status === 'active') ?? []} disableAction disableFilter disableSelection />
				</div>
				<div className="flex mt-5 flex-col justify-center w-full ml-2 border-blue_gray-100_01 border border-solid bg-white-A700 rounded-[10px]">
					<Heading size="2xl" as="h1" className="ml-[43px] mt-[43px]">
						Log Replacements
					</Heading>
					<TableLogPoolReplacements data={logHistory ?? []} />
				</div>
				<div className="flex mt-5 flex-col justify-center w-full ml-2 border-blue_gray-100_01 border border-solid bg-white-A700 rounded-[10px]">
					<Heading size="2xl" as="h1" className="ml-[43px] mt-[43px]">
						Rollover History
					</Heading>
					<TableListMaturityPool data={listMaturity ?? []} onUpdate={_onUpdatePool} />
				</div>
				{data &&
					<div className="flex justify-end">
						<PDFDownloadLink
							document={<DetailPoolPdf data={data}
								log_history={logHistory}
								list_maturity={listMaturity} />
							}
							fileName={`Detail_Pool_${data?.id}.pdf`}
							className="font-bold w-fit text-center cursor-pointer rounded bg-blue-700 text-white-A700 shadow-lg px-4 py-2 m-2"
						>
							{({ loading }) => (loading ? 'Loading document...' : 'Download PDF')}
						</PDFDownloadLink>
					</div>
				}
			</div>
		</div>
	);
};

export default React.memo(DetailPool);
